import { ButtonClassnames } from '@/utils/ClassNameComponent';
import { motion } from 'motion/react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';

interface SidebarItemProps {
  mobile?: boolean;
  text: string;
  icon: JSX.Element;
  url?: string;
  isMenuOpen: boolean;
  testId?: string;
  onClick?: () => void;
  classNames?: ButtonClassnames;
  condition?: boolean;
  setRef?: (ref: HTMLAnchorElement) => void;
}

export default function SidebarItem({ condition, ...props }: SidebarItemProps) {
  const { reorderScheduledLessons, preventExitWithChanges } =
    useCourseEditing();

  const navigate = useNavigate();
  const { text, icon, url, testId, onClick, isMenuOpen } = props;
  const path = window.location.pathname;
  const separator = path.split('/');
  const rootUrl = '/' + separator[1];
  const active = url === '/' ? rootUrl === url : rootUrl.includes(url ?? '');

  const className = twMerge(
    'flex items-center gap-3.5 transition-bg  w-full duration-200  rounded-lg p-1.5 h-8 transition-all duration-200 overflow-hidden max-w-full',
    props.classNames?.base,
    active
      ? twMerge('bg-base-100 text-primary', props.classNames?.active)
      : twMerge(
          'bg-transparent hover:bg-base-100/20 text-base-100',
          props.classNames?.disable,
        ),
  );

  const onClickHandler = () => {
    preventExitWithChanges(() => {
      onClick?.();
      if (url) navigate(url);
    });
  };

  if (condition === false) return <React.Fragment />;

  return (
    <motion.button className={className} onClick={onClickHandler} type="button">
      <ConditionalWrapper
        condition={!!url}
        wrapper={
          <Link
            data-testid={testId}
            to={reorderScheduledLessons.length ? '' : url ?? ''}
            className="flex gap-3.5 items-center"
            ref={props.setRef}
          />
        }
      >
        <Tooltip
          text={text}
          className={twMerge(!isMenuOpen ? 'visible' : 'invisible')}
          options={{
            delayShow: 400,
          }}
        >
          <div className="w-6 h-full shrink-0">{icon}</div>
        </Tooltip>
        <motion.p className={twMerge('!font-600 font-poppins')}>
          {text}
        </motion.p>
      </ConditionalWrapper>
    </motion.button>
  );
}
